<template>
  <div class="tela-cargas">
    <Edit
      :tarefa="currentTarefa"
      :originalTarefa="originalTarefa"
      :update="updateTarefa"
      :truncate="limpaTabelaTarefa"
      :close="closeEdit"
      :crons="crons"
      :descricao="descSingular"
      :modos="modos"
    />
    <Info
      :tarefa="currentTarefa"
      :close="closeInfo"
      :descricao="descSingular"
      :loadTarefas="loadTarefas"
      :syncTarefa="syncTarefa"
    />
    <Loading :isActive="loading"></Loading>

    <!-- Faixa de atenção: só aparece quando há cargas quebradas -->
    <div v-if="qtdQuebradas > 0" class="faixa-atencao">
      <span class="faixa-atencao__msg">
        <fa-icon icon="exclamation-triangle" />
        <strong>{{ qtdQuebradas }}</strong>
        {{ qtdQuebradas === 1 ? 'carga de dados ativa está com falha' : 'cargas de dados ativas estão com falhas' }}
        e {{ qtdQuebradas === 1 ? 'precisa' : 'precisam' }} de atenção.
      </span>
      <button type="button" class="faixa-atencao__atalho" @click="filtroStatus = 'quebradas'">
        Ver com falha →
      </button>
    </div>

    <!-- Barra de ferramentas -->
    <div class="toolbar">
      <span class="toolbar__search">
        <fa-icon icon="search" class="toolbar__search-icon" />
        <input
          type="text"
          v-model="termo"
          placeholder="Filtrar cargas por nome ou descrição..."
        >
      </span>
      <div class="toolbar__right">
        <label class="toolbar__ordenar">
          <span>Ordenar</span>
          <select v-model="ordenacao">
            <option value="prioridade">Prioridade (atenção primeiro)</option>
            <option value="nome">Nome (A–Z)</option>
          </select>
        </label>
        <button class="button button-primary" v-on:click="loadTarefas">
          <fa-icon icon="sync" /> Atualizar
        </button>
      </div>
    </div>

    <!-- Filtro de status (chips) -->
    <StatusFilterBar v-model="filtroStatus" :contagens="contagens" :chips="chipsStatus" />

    <div class="table-responsive">
      <table class="table table-hover tabela-cargas">
        <thead>
        <tr>
          <th class="col-saude"></th>
          <th>Tarefa</th>
          <th>Modo</th>
          <th>Status</th>
          <th>Última execução</th>
          <th>SQL</th>
          <th class="text-right">Ações</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="tarefa in listaTarefas"
          v-bind:key="tarefa.id || tarefa.tarefa"
          :class="{ 'linha-quebrada': saudeDe(tarefa) === 'quebrada' }"
        >
          <td class="col-saude">
            <span
              class="dot-saude"
              :class="`dot-saude--${saudeDe(tarefa)}`"
              :title="tituloSaude(tarefa)"
            ></span>
          </td>
          <td>
            <span class="tarefa-nome">{{ tarefa.tarefa }}</span>
            <small>{{ tarefa.descricao }} | {{ tarefa.cron | humanCron(crons) }}</small>
            <span
              v-if="saudeDe(tarefa) === 'quebrada' && erroInline(tarefa)"
              class="chip-erro"
              :title="erroInline(tarefa)"
            >
              <fa-icon icon="exclamation-triangle" />
              {{ erroInline(tarefa) }}
            </span>
          </td>
          <td><span class="tag-modo">{{ tarefa.modo | humanNcr(modos) }}</span></td>
          <td>
            <span class="label success" v-if="+tarefa.apenasLeitura === 1">Ativo</span>
            <span class="label success" v-else-if="+tarefa.ativo === 1">Ativo</span>
            <span class="label error" v-else>Inativo</span>
          </td>
          <td>
            <div class="ultima-exec-col">
              <template v-if="tarefa.ultimaSincronizacao && tarefa.ultimaSincronizacao.id">
                <span class="ultima-exec" :class="tarefa.ultimaSincronizacao.sucesso ? 'ok' : 'falha'">
                  <fa-icon :icon="tarefa.ultimaSincronizacao.sucesso ? 'check' : 'times'" />
                  {{ relTime(tarefa.ultimaSincronizacao.fim || tarefa.ultimaSincronizacao.inicio) }}
                </span>
                <span v-if="falhasSeguidasDe(tarefa) >= 2" class="badge-falhas">
                  {{ falhasSeguidasDe(tarefa) }} falhas seguidas
                </span>
              </template>
              <span v-else class="ultima-exec nunca">Nunca executada</span>
            </div>
          </td>
          <td>
            <router-link
              v-if="hasSqlErp(tarefa)"
              :to="`/configuracoes/sql-erp-integracao?q=${tarefa.tarefa}`"
              class="btn-ver-sql"
            >
              Ver SQL
            </router-link>
            <span v-else class="text-muted">—</span>
          </td>
          <td class="actions text-right">
            <CButton color="primary" size="sm" v-on:click="openEdit(tarefa)" v-if="!tarefa.apenasLeitura" title="Editar">
              <fa-icon icon="pencil-alt" />
            </CButton>
            <CButton color="success" size="sm" @click="syncTarefa(tarefa)" title="Sincronizar agora">
              <fa-icon icon="sync"/>
            </CButton>
            <CButton v-bind:color="tarefa | tipoButtonTarefa(tarefa)" size="sm" v-on:click="openInfo(tarefa)" v-bind:title="tarefa | titleTarefa">
              <fa-icon icon="info-circle"/>
            </CButton>
            <LogAlteracaoDados tabela="tarefa" :chave="tarefa.id">
              <template #trigger="{ open }">
                <CButton
                  color="secondary"
                  variant="outline"
                  size="sm"
                  class="btn-acao-log"
                  title="Log de alterações"
                  @click="open"
                >
                  <fa-icon icon="clipboard-list" />
                </CButton>
              </template>
            </LogAlteracaoDados>
          </td>
        </tr>
        <tr v-if="listaTarefas.length === 0">
          <td colspan="7" class="vazio">Nenhuma carga encontrada para este filtro.</td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="rodape-contagem">
      {{ listaTarefas.length }} de {{ tarefas.length }} cargas
    </div>
  </div>
</template>
<script>
import clone from 'just-clone';
import { get, post, put } from '@/helpers/apiRequest';
import Edit from './Edit';
import Info from './Info';
import StatusFilterBar from '@/components/StatusFilterBar';
import { CRONS, MODOS } from './constantes';
// import Validate from './Validate';
import { normalizeString, arrayFilter } from '@/helpers/utils';
import {
  statusOf,
  matchFilter,
  relTime,
  pesoPrioridade,
} from '@/helpers/cargasSaude';
import { interpretarErroSincronizacao } from '@/helpers/sincronizacaoErros';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';

export default {
  name: 'tarefas',
  components: {
    // Validate,
    Edit,
    Info,
    StatusFilterBar,
  },
  data() {
    return {
      termo: '',
      loading: false,
      filtroStatus: 'todas',
      // chips do StatusFilterBar (cor = dot + estado ativo; borda = acento idle)
      chipsStatus: [
        { chave: 'todas', label: 'Todas', cor: '#3a3e81' },
        { chave: 'ativas', label: 'Ativas', cor: '#3a3e81' },
        { chave: 'inativas', label: 'Inativas', cor: '#5a6072' },
        { chave: 'sucesso', label: 'Com sucesso', dot: true, cor: '#2a8a52', borda: '#bfe3cc' },
        { chave: 'quebradas', label: 'Com falha', dot: true, cor: '#c0392b', borda: '#f3cdc8' },
      ],
      ordenacao: 'prioridade',
      // mapa { [nomeTarefa]: <int falhas seguidas> } carregado em UMA única
      // requisição para as cargas quebradas (substitui N chamadas de log-execução).
      falhasSeguidasMap: {},
      currentTarefa: {},
      originalTarefa: {},
      lastValidation: '',
      tarefas: [],
      descPlural: 'Tarefa',
      descSingular: 'Tarefas',
      crons: CRONS,
      modos: MODOS,
    };
  },
  computed: {
    listaTarefas() {
      // 1. busca textual + 2. filtro de status (interseção) + 3. ordenação
      const porTexto = arrayFilter(this.tarefas, this.termo, ['descricao', 'tarefa', 'modo']);
      const porStatus = porTexto.filter((t) => matchFilter(t, this.filtroStatus));

      const ordenada = porStatus.slice();
      if (this.ordenacao === 'nome') {
        ordenada.sort((a, b) =>
          `${a.tarefa}`.localeCompare(`${b.tarefa}`, 'pt-BR', { sensitivity: 'base' })
        );
      } else {
        // prioridade: atenção primeiro (quebrada > nunca > sucesso > inativa), depois nome
        ordenada.sort((a, b) => {
          const peso = pesoPrioridade(a) - pesoPrioridade(b);
          if (peso !== 0) {
            return peso;
          }
          return `${a.tarefa}`.localeCompare(`${b.tarefa}`, 'pt-BR', { sensitivity: 'base' });
        });
      }

      return ordenada;
    },
    contagens() {
      const chaves = ['todas', 'ativas', 'inativas', 'sucesso', 'quebradas'];
      const resultado = {};
      chaves.forEach((chave) => {
        resultado[chave] = this.tarefas.filter((t) => matchFilter(t, chave)).length;
      });
      return resultado;
    },
    qtdQuebradas() {
      return this.tarefas.filter((t) => statusOf(t) === 'quebrada').length;
    },
  },
  filters: {
    humanCron(cron, crons) {
      return crons[cron] || cron;
    },
    humanNcr(modo, modos) {
      return modos[modo]?.alias || modo;
    },
    tipoButtonTarefa(tarefa) {
      const { ultimaSincronizacao } = tarefa;

      if (ultimaSincronizacao &&
          ultimaSincronizacao.id &&
          !ultimaSincronizacao.sucesso) {
        return 'danger';
      }

      if (ultimaSincronizacao && ultimaSincronizacao.sucesso) {
        return 'info';
      }

      return 'secondary';
    },

    titleTarefa(tarefa) {
      const { ultimaSincronizacao } = tarefa;

      if (ultimaSincronizacao && ultimaSincronizacao.msg) {
        return ultimaSincronizacao.msg;
      }

      return 'Nunca sincronizada';
    },
  },
  watch: {
    "termo": function(newValue) {
      history.pushState({}, null, this.$route.path + '?q=' + (newValue || ''));
    },
  },
  methods: {
    // ---- saúde / utilitários de exibição --------------------------------
    saudeDe(tarefa) {
      return statusOf(tarefa);
    },
    relTime(timestamp) {
      return relTime(timestamp);
    },
    tituloSaude(tarefa) {
      const titulos = {
        sucesso: 'Última sincronização concluída com sucesso',
        quebrada: 'A última sincronização falhou',
        inativa: 'Carga inativa',
        nunca: 'Carga ativa, mas nunca sincronizada',
      };
      return titulos[this.saudeDe(tarefa)] || '';
    },
    erroInline(tarefa) {
      const ultima = tarefa && tarefa.ultimaSincronizacao;
      if (!ultima || !ultima.msg) {
        return '';
      }
      // Memoiza por mensagem: erroInline é chamado por linha a cada re-render
      // (busca, ordenação...) e interpretarErroSincronizacao roda dezenas de regex.
      const msg = String(ultima.msg);
      if (Object.prototype.hasOwnProperty.call(this._erroInlineCache, msg)) {
        return this._erroInlineCache[msg];
      }
      const erro = interpretarErroSincronizacao(msg);
      const titulo = erro ? erro.titulo : 'Falha na última sincronização';
      this._erroInlineCache[msg] = titulo;
      return titulo;
    },
    falhasSeguidasDe(tarefa) {
      return this.falhasSeguidasMap[tarefa.tarefa] || 0;
    },
    // Conta as falhas seguidas das cargas quebradas em UMA única requisição
    // (em vez de uma chamada de log-execução por carga). Roda em segundo plano:
    // a tabela já está visível e os badges preenchem quando a resposta chega.
    carregarHistoricosQuebradas() {
      this.falhasSeguidasMap = {};
      const quebradas = this.tarefas.filter((t) => statusOf(t) === 'quebrada');

      if (quebradas.length === 0) {
        return;
      }

      const nomes = quebradas.map((t) => t.tarefa).join(',');
      get(`/admin/tarefas/falhas-seguidas?tarefas=${encodeURIComponent(nomes)}`)
        .then((mapa) => {
          this.falhasSeguidasMap = mapa && typeof mapa === 'object' ? mapa : {};
        })
        .catch(() => {
          // silencioso: a ausência do dado apenas oculta o badge
        });
    },
    loadDescricaoModulo() {
      const { modulos } = JSON.parse(localStorage.getItem('app'));
      const { descPlural, descSingular } = modulos.find(
        (m) => m.nome === 'ADMIN_CARGA_DADOS'
      );
      this.descPlural = descPlural;
      this.descSingular = descSingular;
    },
    openEdit(tarefa) {
      this.currentTarefa = clone(tarefa);
      this.originalTarefa = {'ativo': tarefa.ativo}
      this.$modal.show('tarefaEdit');
    },
    closeEdit() {
      this.$modal.hide('tarefaEdit');
    },
    openInfo(tarefa) {
      this.currentTarefa = tarefa;
      this.$modal.show('tarefaInfo');
    },
    closeInfo() {
      this.$modal.hide('tarefaInfo');
    },
    openValidate() {
      this.$modal.show('tarefaValidate');
    },
    closeValidate() {
      this.$modal.hide('tarefaValidate');
    },
    loadTarefas() {
      this.loading = true;
      get('/admin/tarefas')
        .then((json) => (this.tarefas = json))
        .catch(() => (this.tarefas = []))
        .then(() => (this.loading = false))
        .catch(() => (this.loading = false))
        .then(this.changeEditingTask)
        .then(this.carregarHistoricosQuebradas);
    },
    changeEditingTask() {
      if (this.currentTarefa) {
        let tarefa = _find(this.tarefas, { id: this.currentTarefa.id });
        if (tarefa) {
          this.currentTarefa = clone(tarefa);
        }
      }
    },
    updateTarefa() {
      const { id } = this.currentTarefa;
      this.loading = true;
      let request = put(`/admin/tarefas/${id}`, this.currentTarefa);
      request.then(() => this.loadTarefas());
      request.catch(() => (this.loading = false));
      return request;
    },
    limpaTabelaTarefa() {
      const { id, tabela } = this.currentTarefa;

      this.$swal({
        title: 'Confirma Limpar tabela ?',
        text: 'Essa ação irá excluir todos os registros da tabela "'+ tabela +'"',
        showCancelButton: true,
        icon: 'warning',
      })
        .then((res) => {
          if (res.value) {
            this.loading = true;
            post(`/admin/tarefas/truncate/${id}`, this.currentTarefa)
              .then((json) => {
                  this.loading = false;
                  this.$swal({
                    title: 'Sucesso!',
                    text: json.message,
                    icon: 'success',
                    confirmButtonText: 'Fechar',
                  });
                })
                .catch((error) => {
                  this.loading = false;
                  this.$swal({
                    title: error.message,
                    text: 'Desative e atualize a carga para continuar',
                    icon: 'warning',
                    confirmButtonText: 'Fechar',
                  });
                })
          }
        })
        .catch(() => {
        });
    },
    syncTarefa(tarefa) {
      const { id } = tarefa;
      this.loading = true;
      post(`/admin/tarefas/sync/${id}`)
        .then((json) => {
          this.loading = false;
          this.$swal({
            title: 'Sucesso!',
            text: json.message,
            icon: 'success',
            confirmButtonText: 'Fechar',
          });
        })
        .catch((error) => {
          this.loading = false;
          this.$swal({
            title: error.message,
            text: 'Tente novamente em alguns instantes',
            icon: 'warning',
            confirmButtonText: 'Fechar',
          });
        });
    },
    validateViews() {
      this.loading = true;
      get(`/admin/tarefas/validate`)
        .then((json) => {
          this.openValidate();
          this.loading = false;
          this.lastValidation = json.message;
        })
        .catch((error) => {
          this.loading = false;
          this.$swal({
            title: error.message,
            text: 'Tente novamente em alguns instantes',
            icon: 'warning',
            confirmButtonText: 'Fechar',
          });
        });
    },
    hasSqlErp(tarefa) {
      return !_isEmpty(tarefa.sqlErp);
    }
  },
  created() {
    // cache não-reativo da interpretação de erro (chave = mensagem)
    this._erroInlineCache = {};
  },
  beforeMount() {
    this.termo = this.$route.query.q || '';
    const status = this.$route.query.status;
    if (['todas', 'ativas', 'inativas', 'sucesso', 'quebradas'].includes(status)) {
      this.filtroStatus = status;
    }
    this.loadTarefas();
    this.loadDescricaoModulo();
  },
};
</script>

<style scoped>
/* Tokens VersoTech (escopo da tela de Cargas).
   Os blocos compartilhados entre as abas (toolbar, dot-saude, tarefa-nome,
   tag-modo, ultima-exec, linha-quebrada, vazio, btn-acao-log...) vivem no
   parcial src/assets/scss/_cargas.scss, sob a classe raiz .tela-cargas. */
.faixa-atencao {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 16px;
  padding: 10px 14px;
  background: #fdeaea;
  border: 1px solid #f3cdc8;
  border-left: 4px solid var(--verso-falha, #c0392b);
  border-radius: 6px;
  color: #8c2a20;
}

.faixa-atencao__msg {
  font-size: 14px;
}

.faixa-atencao__msg .fa-icon,
.faixa-atencao__msg svg {
  color: var(--verso-falha, #c0392b);
  margin-right: 4px;
}

.faixa-atencao__atalho {
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--verso-falha, #c0392b);
  background: transparent;
  border: 1px solid #e3a9a2;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}

.faixa-atencao__atalho:hover {
  background: #f9dcd9;
}

.chip-erro {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
  max-width: 360px;
  padding: 3px 8px;
  font-size: 12px;
  color: #8c2a20;
  background: #fdeaea;
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip-erro svg {
  color: var(--verso-falha, #c0392b);
  flex-shrink: 0;
}

.badge-falhas {
  display: inline-block;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--verso-falha, #c0392b);
  background: #fdeaea;
  border-radius: 999px;
}

/* Botão "Ver SQL" (pílula teal) */
.btn-ver-sql {
  display: inline-block;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #1a7a83;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
}

.btn-ver-sql:hover {
  color: #15646c;
  background: #dcf0f3;
  text-decoration: none;
}

.rodape-contagem {
  margin-top: 12px;
  font-size: 13px;
  color: var(--verso-neutro, #949aa8);
}

.text-muted {
  color: var(--verso-neutro, #949aa8);
}

/* Espaçamento entre os botões de ação desta tabela */
.actions ::v-deep .btn {
  margin-left: 5px;
}
</style>
